import { useState } from 'react';
import type { ReactNode } from 'react';
import { FiArrowRight } from 'react-icons/fi';

import type { ApiException } from '@/common/error';
import { Avatar, Modal } from '@/components';
import { FormButton, FormButtonOutlined } from '@/components/FormInputs/FormInputs';
import { useAssignSeatMutation } from '@/store/people/people.store';
import {
  useSubscriptionPricesQuery,
  useUpdateSubscriptionMutation,
} from '@/store/subscriptions/subscriptions.store';
import { SubscriptionTier } from '@/store/subscriptions/subscriptions.types';
import type { Subscription } from '@/store/subscriptions/subscriptions.types';

import { STATUS_LABEL } from '../subscription.constants';
import { INTERVAL_UNIT } from '../subscription.display';
import { fmtDate } from '../subscription.helpers';
import s from '../subscription.module.scss';
import {
  crewLookupKey,
  fmtPrice,
  planBill,
  planLookupKeys,
  pricedPlan,
  seatLookupKey,
  unitPrice,
} from '../subscription.pricing';
import type { PricedPlan } from '../subscription.pricing';
import { SEAT_POOL_LABEL, canVacate, displayName, seatOffer } from '../subscription.seats';
import type { SeatPerson } from '../subscription.seats';
import type { Vessel } from '../subscription.types';
import { hasFreeSeat } from '../useVesselSeats';
import type { SeatPoolState, VesselSeats } from '../useVesselSeats';

import { VesselPlanModal } from './VesselPlanModal';

const AVATAR_SIZE = 34;

/**
 * Same single error `type` as assigning. Eligibility, capacity and the caller's rights were
 * all checked before the drop was accepted, so a stale view is what is left: the pool filled
 * up meanwhile, or the plan changed underneath.
 */
const errorMessage = (error: unknown, fallback: string) =>
  (error as ApiException | null)?.type === 'NOT_FOUND'
    ? 'That person is no longer on this vessel.'
    : fallback;

interface MoveProps {
  person: SeatPerson;
  /** The pool they leave; left out when they hold no seat and are being given one. */
  from?: SeatPoolState;
  to: SeatPoolState;
  vessel: Vessel;
  seats: VesselSeats;
  onClose: () => void;
}

/** The copy that differs between moving a seated person and giving someone with none a seat. */
const wording = (from: SeatPoolState | undefined) =>
  from
    ? { action: 'Move to', verb: 'move', pending: 'Moving…', buy: 'Buy seat & move' }
    : { action: 'Give', verb: 'give', pending: 'Giving seat…', buy: 'Buy & give seat' };

/** A count or amount before and after the change, the new one in bold. */
function ChangeRow({
  label,
  before,
  after,
}: {
  label: string;
  before: ReactNode;
  after: ReactNode;
}) {
  return (
    <div className={s.impactRow}>
      <span>{label}</span>
      <span className={s.impactCount}>
        {before}
        <FiArrowRight aria-hidden />
        <strong>{after}</strong>
      </span>
    </div>
  );
}

/**
 * Who is moving and between which seats. A plain move strikes the old seat through; into a
 * full pool nothing has moved yet, so the target is flagged with its count instead.
 */
function MoveSummary({ person, from, to, vessel, full }: MoveProps & { full?: boolean }) {
  const fromLabel = from ? `${SEAT_POOL_LABEL[from.pool]} seat` : 'No seat';
  const toLabel = `${SEAT_POOL_LABEL[to.pool]} seat`;

  return (
    <>
      <div className={s.personCard}>
        <Avatar
          src={person.avatar}
          firstName={person.firstName}
          lastName={person.lastName}
          size={AVATAR_SIZE}
        />
        <span className={s.candidateText}>
          <span className={s.candidateName}>{displayName(person)}</span>
          <span className={s.candidateRole}>{vessel.name}</span>
        </span>
      </div>

      <div className={s.seatChange}>
        <span className={s.sectionTitle}>Seat change</span>
        <div className={s.seatPills}>
          <span className={full ? s.seatPill : s.seatPillFrom}>{fromLabel}</span>
          <FiArrowRight aria-label="to" />
          {full ? (
            <span className={s.seatPillFull}>
              {toLabel} · {to.occupants.length}/{to.capacity}
            </span>
          ) : (
            <span className={s.seatPillTo}>{toLabel}</span>
          )}
        </div>
      </div>
    </>
  );
}

/** The plan as it stands, ruled off from the person and seats above it. */
function PlanImpact({
  vessel,
  seats,
  children,
}: {
  vessel: Vessel;
  seats: VesselSeats;
  children: ReactNode;
}) {
  return (
    <div className={s.impact}>
      <span className={s.sectionTitle}>Plan impact</span>
      <p className={s.impactPlan}>
        <strong>{vessel.plan}</strong> · {STATUS_LABEL[vessel.status]} · {seats.occupied}/
        {seats.capacity} seats used
      </p>
      {children}
    </div>
  );
}

const occupancy = ({ occupants, capacity }: SeatPoolState, delta = 0) =>
  `${occupants.length + delta}/${capacity}`;

function MoveSeatForm(props: MoveProps) {
  const { person, from, to, vessel, seats, onClose } = props;
  const assign = useAssignSeatMutation();
  const words = wording(from);

  const confirm = () => {
    if (!to.seatType) return;
    // The server moves a person who holds the other seat type rather than seating them twice,
    // so this one call both frees the old seat and fills the new one. Closing on success lets
    // the invalidated queries repaint both rows underneath.
    assign.mutate(
      { personId: person.personId, serviceId: vessel.id, seatType: to.seatType },
      { onSuccess: onClose },
    );
  };

  return (
    <div className={s.modalBody}>
      <MoveSummary {...props} />

      <PlanImpact vessel={vessel} seats={seats}>
        {from && (
          <ChangeRow
            label={SEAT_POOL_LABEL[from.pool]}
            before={occupancy(from)}
            after={occupancy(from, -1)}
          />
        )}
        <ChangeRow
          label={SEAT_POOL_LABEL[to.pool]}
          before={occupancy(to)}
          after={occupancy(to, 1)}
        />
        {from && (
          <p className={s.formHint}>
            A {SEAT_POOL_LABEL[from.pool]} seat is freed and can be given to someone else.
          </p>
        )}
      </PlanImpact>

      {assign.isError && (
        <p className={s.formError}>
          {errorMessage(
            assign.error,
            `Could not ${words.verb} the seat. The ${SEAT_POOL_LABEL[to.pool]} pool may have just filled up, or the plan changed — reopen to see where things stand.`,
          )}
        </p>
      )}

      <div className={s.modalActions}>
        <FormButtonOutlined onClick={onClose} disabled={assign.isPending}>
          Cancel
        </FormButtonOutlined>
        <FormButton onClick={confirm} disabled={assign.isPending}>
          {assign.isPending ? words.pending : 'Confirm seat change'}
        </FormButton>
      </div>
    </div>
  );
}

/** Why a full pool cannot take them as it stands — and, when there is one, the free way out. */
const fullReason = (to: SeatPoolState, plan: string, name: string) => {
  const label = SEAT_POOL_LABEL[to.pool];
  const taken =
    to.capacity === 0
      ? `Your ${plan} plan has no ${label} seats yet.`
      : to.capacity === 1
        ? `The only ${label} seat on your ${plan} plan is taken.`
        : `All ${to.capacity} ${label} seats on your ${plan} plan are taken.`;
  // The owner's seat cannot be emptied, so freeing one is only suggested when it can be done.
  const orFree = to.occupants.some((person) => canVacate(person, to.viewerPersonId))
    ? `, or free a ${label} seat first`
    : '';
  return `${taken} Add a seat for ${name}${orFree}.`;
};

/**
 * When the extra seat is paid for. A trial bills nothing until it ends; past it, the
 * server invoices a seat increase straight away, prorated to the end of the period.
 */
const chargeNote = ({ status, renewal }: Vessel) => {
  const date = renewal.kind === 'renews' ? fmtDate(renewal.at) : null;
  if (status === 'trial') {
    return date
      ? `Free until your trial ends on ${date}, then billed with your plan.`
      : 'Free until your trial ends, then billed with your plan.';
  }
  return date
    ? `Charged today, prorated to ${date}, then billed with your plan.`
    : 'Charged today for the rest of this period, then billed with your plan.';
};

/**
 * Buys one more seat in the target pool, then moves the person into it — two calls, since
 * the plan and the seats live behind different endpoints. They are not atomic: when the buy
 * lands and the move does not, the seat is kept and paid for, so the retry only moves.
 */
function BuySeatForm({
  subscription,
  plan,
  occupantsNow,
  ...props
}: MoveProps & {
  subscription: Subscription;
  plan: PricedPlan;
  /** Who sits in the target pool now — `to` is the pool as it stood on the drop. */
  occupantsNow: SeatPerson[];
}) {
  const { person, from, to, vessel, seats, onClose } = props;
  const buy = useUpdateSubscriptionMutation();
  const assign = useAssignSeatMutation();
  const words = wording(from);

  const label = SEAT_POOL_LABEL[to.pool];
  const name = displayName(person);
  const isManagement = to.pool === 'management';
  const count = isManagement ? 'seats' : 'crewSeats';
  const after = { ...plan, [count]: plan[count] + 1 };

  // The plan with the extra seat needs every price the current one does, plus the new line's.
  const { data: prices } = useSubscriptionPricesQuery(vessel.id, planLookupKeys(after));
  const unit = INTERVAL_UNIT[plan.interval];
  const seat = unitPrice(
    prices,
    isManagement ? seatLookupKey(plan.tier, plan.interval) : crewLookupKey(plan.interval),
  );
  const price = seat && !Number.isNaN(seat.amount) ? fmtPrice(seat.amount, seat.currency) : null;
  const billBefore = planBill(plan, prices);
  const billAfter = planBill(after, prices);

  const move = () => {
    if (!to.seatType) return;
    assign.mutate(
      { personId: person.personId, serviceId: vessel.id, seatType: to.seatType },
      { onSuccess: onClose },
    );
  };

  // `applied` means the webhook has written the new count, so the seat exists by the time the
  // move asks for it. Short of that — a declined card, a late webhook — nothing is moved.
  const bought = buy.data?.applied === true;
  const confirm = () => {
    if (bought) return move();
    buy.mutate(
      {
        serviceId: vessel.id,
        id: subscription.id,
        // Only the count that changes: the other one is as it stood on the drop, and sending it
        // would undo a change made elsewhere since. The owner answer is the exception — left
        // out, it is cleared, un-reserving the owner's seat (`UpdateSubscriptionDto`).
        input: {
          ...(isManagement ? { seats: after.seats } : { crewSeats: after.crewSeats }),
          ...(!vessel.ownerId && { willAddOwner: subscription.captainWillAddOwner }),
        },
      },
      {
        onSuccess: ({ applied }) => {
          if (applied) move();
        },
      },
    );
  };

  // A new seat goes first to anyone the plan owes one — the server seats a waiting co-owner
  // before this move can land. Whoever arrived in the pool since the drop is who took it.
  const takenBy = assign.isError
    ? occupantsNow.find(
        ({ personId }) =>
          personId !== person.personId && !to.occupants.some((o) => o.personId === personId),
      )
    : undefined;

  const isPending = buy.isPending || assign.isPending;
  const submitLabel = () => {
    if (buy.isPending) return 'Adding seat…';
    if (assign.isPending) return words.pending;
    if (bought) return `${words.action} ${label} seat`;
    return price ? `${words.buy} · ${price}${unit}` : words.buy;
  };

  return (
    <div className={s.modalBody}>
      <MoveSummary {...props} full />

      <PlanImpact vessel={vessel} seats={seats}>
        <p className={s.impactPlan}>{fullReason(to, vessel.plan, name)}</p>

        <div className={s.seatOffer}>
          <span className={s.seatOfferText}>
            <strong>+1 {label} seat</strong>
            {chargeNote(vessel)}
          </span>
          {/* No price, no figure — and no buying either, see the button below. */}
          {price && (
            <span className={s.seatOfferPrice}>
              <strong>{price}</strong>
              {unit}
            </span>
          )}
        </div>

        <ChangeRow label={`${label} seats`} before={plan[count]} after={after[count]} />
        {billBefore && billAfter && (
          <ChangeRow
            label="Plan total"
            before={fmtPrice(billBefore.amount, billBefore.currency)}
            after={`${fmtPrice(billAfter.amount, billAfter.currency)}${unit}`}
          />
        )}
      </PlanImpact>

      {(buy.isError || (buy.isSuccess && !bought)) && (
        <p className={s.formError}>
          The {label} seat has not been added. If your card was declined, update it in billing, then
          try again.
        </p>
      )}
      {assign.isError && (
        <p className={s.formError}>
          {takenBy
            ? `The ${label} seat was added, but ${displayName(takenBy)} was given it first. Start again to add another for ${name}.`
            : errorMessage(
                assign.error,
                `The ${label} seat was added, but ${name} could not be put in it yet. Try again in a moment.`,
              )}
        </p>
      )}

      <div className={s.modalActions}>
        <FormButtonOutlined onClick={onClose} disabled={isPending}>
          Cancel
        </FormButtonOutlined>
        {/* Never bought unpriced: the price is the one thing this asks the owner to agree to.
            Nor retried into a seat someone else now holds — that only fails again. */}
        <FormButton onClick={confirm} disabled={isPending || (!bought && !price) || !!takenBy}>
          {submitLabel()}
        </FormButton>
      </div>
    </div>
  );
}

/**
 * Navigate's one management seat cannot be added to, so the way in is Command. The upgrade
 * itself is the plan form's job; once it lands the person is dragged in again.
 */
function UpgradeSeatForm({ onUpgrade, ...props }: MoveProps & { onUpgrade: () => void }) {
  const { person, vessel, seats, onClose } = props;

  return (
    <div className={s.modalBody}>
      <MoveSummary {...props} full />

      <PlanImpact vessel={vessel} seats={seats}>
        <p className={s.impactPlan}>
          Navigate is single-user, so its one Management seat is all it can hold. Upgrade to Command
          for more Management seats, then put {displayName(person)} in one.
        </p>
      </PlanImpact>

      <div className={s.modalActions}>
        <FormButtonOutlined onClick={onClose}>Cancel</FormButtonOutlined>
        <FormButton onClick={onUpgrade}>Upgrade to Command</FormButton>
      </div>
    </div>
  );
}

/**
 * Confirms moving someone from one seat pool to the other, or giving someone with no seat one —
 * reached from their face's menu or by dropping the face on a pool's row. A drop is easy to make
 * by accident, so it asks before it changes anything — and says what it does to the plan's counts.
 *
 * Dropped on a full pool, it offers the way to make room instead: buy a seat there, or, for
 * Navigate's management pool, upgrade the plan.
 */
export function MoveSeatModal({
  open,
  subscription,
  ...props
}: MoveProps & {
  open: boolean;
  subscription: Subscription | null;
}) {
  const { to, onClose } = props;
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Settled on the drop and then held, along with the pool, seats and plan as they stood:
  // buying the seat refetches all of them, and the offer on screen has to stay the one being
  // accepted rather than turn into a plain move or re-price itself halfway through.
  const [held] = useState(() => ({
    offer: hasFreeSeat(to) ? null : seatOffer(subscription, to.pool),
    plan: pricedPlan(subscription),
    to,
    seats: props.seats,
  }));
  const { offer, plan } = held;

  if (isUpgrading) {
    return (
      <VesselPlanModal
        open={open}
        vessel={props.vessel}
        subscription={subscription}
        start={{ tier: SubscriptionTier.COMMAND, seats: held.to.capacity + 1 }}
        onClose={onClose}
      />
    );
  }

  const label = SEAT_POOL_LABEL[to.pool];
  const fullProps = { ...props, to: held.to, seats: held.seats };

  const body = () => {
    if (offer === 'buy' && plan && subscription) {
      return (
        <BuySeatForm
          {...fullProps}
          subscription={subscription}
          plan={plan}
          occupantsNow={to.occupants}
        />
      );
    }
    if (offer === 'upgrade') {
      return <UpgradeSeatForm {...fullProps} onUpgrade={() => setIsUpgrading(true)} />;
    }
    return <MoveSeatForm {...props} />;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        offer ? (
          <>
            <span className={s.eyebrow}>No free seat</span>
            {label} is full
          </>
        ) : (
          `${wording(props.from).action} ${label} seat?`
        )
      }
    >
      {body()}
    </Modal>
  );
}
